/**
 * ricmejias-2026 — main front-end bundle.
 *
 * Responsibilities:
 *   - progressive reveal choreography (GSAP + ScrollTrigger)
 *   - smooth scrolling (Lenis) wired into ScrollTrigger
 *   - header state, scroll progress, mobile menu
 *   - the 3-cores tab interface (accessible)
 *   - split-text headlines, counters, copy-to-clipboard
 *   - deciding whether to download the WebGL bundle at all
 *
 * Everything motion-related is registered inside gsap.matchMedia() so
 * `prefers-reduced-motion: reduce` gets a complete, static page.
 */

import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Lenis from 'lenis';
import { splitText, collectChars } from './split.js';

gsap.registerPlugin(ScrollTrigger);

const DATA = window.RM_DATA || {};
const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/* ==========================================================================
   Smooth scroll
   ========================================================================== */

function initSmoothScroll() {
  if (prefersReduced) return null;

  const lenis = new Lenis({
    lerp: 0.1,
    wheelMultiplier: 1,
    touchMultiplier: 1.6,
  });

  lenis.on('scroll', ScrollTrigger.update);

  gsap.ticker.add((time) => lenis.raf(time * 1000));
  gsap.ticker.lagSmoothing(0);

  // In-page anchors should go through Lenis, and respect the fixed header.
  document.querySelectorAll('a[href^="#"]').forEach((a) => {
    const href = a.getAttribute('href');
    if (!href || href === '#' || a.hasAttribute('data-no-smooth')) return;

    a.addEventListener('click', (e) => {
      const target = document.querySelector(href);
      if (!target) return;
      e.preventDefault();
      lenis.scrollTo(target, { offset: -70, duration: 1.1 });
      history.replaceState(null, '', href);
    });
  });

  return lenis;
}

/* ==========================================================================
   Header, scroll progress, mobile menu
   ========================================================================== */

function initChrome() {
  const header = document.getElementById('site-header');
  const progress = document.querySelector('.scroll-progress');
  const toggle = document.querySelector('[data-nav-toggle]');
  const menu = document.querySelector('[data-mobile-menu]');

  // Header background once we leave the hero.
  if (header) {
    const onScroll = () => {
      header.classList.toggle('is-stuck', window.scrollY > 40);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  // Reading progress.
  if (progress) {
    const update = () => {
      const max = document.documentElement.scrollHeight - window.innerHeight;
      const value = max > 0 ? Math.min(1, window.scrollY / max) : 0;
      progress.style.setProperty('--progress', value.toFixed(4));
    };
    update();
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
  }

  // Mobile menu.
  if (toggle && menu) {
    const setOpen = (open) => {
      toggle.setAttribute('aria-expanded', String(open));
      menu.hidden = false;
      menu.classList.toggle('is-open', open);
      document.documentElement.style.overflow = open ? 'hidden' : '';

      if (!open) {
        window.setTimeout(() => {
          if (toggle.getAttribute('aria-expanded') === 'false') menu.hidden = true;
        }, 600);
      }
    };

    toggle.addEventListener('click', () => {
      setOpen(toggle.getAttribute('aria-expanded') !== 'true');
    });

    menu.addEventListener('click', (e) => {
      if (e.target.closest('a')) setOpen(false);
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && toggle.getAttribute('aria-expanded') === 'true') {
        setOpen(false);
        toggle.focus();
      }
    });
  }
}

/* ==========================================================================
   Reveals
   ========================================================================== */

function initReveals() {
  const ease = 'expo.out';

  gsap.matchMedia().add('(prefers-reduced-motion: no-preference)', () => {
    const mark = (el) => el.setAttribute('data-revealed', 'true');

    // Single elements.
    document.querySelectorAll('[data-reveal]').forEach((el) => {
      const kind = el.getAttribute('data-reveal') || 'up';
      const from = {
        up: { y: 26, opacity: 0 },
        down: { y: -20, opacity: 0 },
        left: { x: -30, opacity: 0 },
        right: { x: 30, opacity: 0 },
        scale: { scale: 0.965, opacity: 0 },
        fade: { opacity: 0 },
      }[kind] || { y: 26, opacity: 0 };

      if (kind === 'clip') {
        gsap.fromTo(
          el,
          { clipPath: 'inset(0 0 100% 0)' },
          {
            clipPath: 'inset(0 0 0% 0)',
            duration: 1.1,
            ease,
            scrollTrigger: { trigger: el, start: 'top 86%', once: true },
            onComplete: () => mark(el),
          }
        );
        gsap.set(el, { opacity: 1 });
        mark(el);
        return;
      }

      gsap.fromTo(
        el,
        from,
        {
          x: 0,
          y: 0,
          scale: 1,
          opacity: 1,
          duration: 0.95,
          ease,
          scrollTrigger: { trigger: el, start: 'top 88%', once: true },
          onComplete: () => mark(el),
        }
      );
    });

    // Staggered children.
    document.querySelectorAll('[data-reveal-child]').forEach((wrap) => {
      const kids = Array.from(wrap.children);
      if (!kids.length) return;

      gsap.fromTo(
        kids,
        { y: 22, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.8,
          ease,
          stagger: 0.075,
          scrollTrigger: { trigger: wrap, start: 'top 85%', once: true },
          onComplete: () => kids.forEach(mark),
        }
      );
    });

    return () => {
      ScrollTrigger.getAll().forEach((t) => t.kill());
    };
  });

  // Guarantee visibility if motion is reduced or GSAP never ran.
  if (prefersReduced) {
    document.querySelectorAll('[data-reveal], [data-reveal-child] > *').forEach((el) => {
      el.setAttribute('data-revealed', 'true');
    });
  }
}

/* ==========================================================================
   Split-text headlines
   ========================================================================== */

function initSplits() {
  gsap.matchMedia().add('(prefers-reduced-motion: no-preference)', () => {
    // Hero headline: lines rise from behind a mask on load.
    document.querySelectorAll('.hero__title[data-split="lines"]').forEach((el) => {
      const lines = splitText(el, 'lines');
      if (!lines.length) return;

      gsap.set(el, { opacity: 1 });
      gsap.from(lines, {
        yPercent: 118,
        duration: 1.15,
        ease: 'expo.out',
        stagger: 0.09,
        delay: 0.15,
      });
      lines.forEach((l) => l.setAttribute('data-revealed', 'true'));
    });

    // Credo statement: words fade up as you scroll into it.
    document.querySelectorAll('[data-split="words"]').forEach((el) => {
      const words = splitText(el, 'words');
      if (!words.length) return;
      gsap.set(el, { opacity: 1 });
      gsap.from(words, {
        opacity: 0,
        y: 14,
        duration: 0.55,
        ease: 'power2.out',
        stagger: 0.012,
        scrollTrigger: { trigger: el, start: 'top 78%', once: true },
      });
    });

    // WILLINGABLE: characters rise and settle.
    document.querySelectorAll('[data-split="chars"]').forEach((el) => {
      const chars = collectChars(el);
      if (!chars.length) return;
      gsap.set(el, { opacity: 1 });
      gsap.from(chars, {
        yPercent: 105,
        opacity: 0,
        rotate: 4,
        duration: 1.0,
        ease: 'back.out(1.5)',
        stagger: 0.045,
        scrollTrigger: { trigger: el, start: 'top 82%', once: true },
      });
    });
  });
}

/* ==========================================================================
   Counters
   ========================================================================== */

function initCounters() {
  const targets = document.querySelectorAll('[data-count]');
  if (!targets.length) return;

  if (prefersReduced) {
    targets.forEach((t) => {
      const out = t.querySelector('[data-count-target]');
      if (out) out.textContent = t.getAttribute('data-count');
    });
    return;
  }

  targets.forEach((wrap) => {
    const out = wrap.querySelector('[data-count-target]');
    const raw = wrap.getAttribute('data-count');
    const end = parseFloat(raw);
    if (!out || Number.isNaN(end)) return;

    const obj = { v: 0 };
    gsap.to(obj, {
      v: end,
      duration: 1.8,
      ease: 'expo.out',
      snap: { v: 1 },
      scrollTrigger: { trigger: wrap, start: 'top 92%', once: true },
      onUpdate: () => {
        out.textContent = String(Math.round(obj.v));
      },
      onComplete: () => {
        out.textContent = String(end);
      },
    });
  });
}

/* ==========================================================================
   The 3-cores tab interface
   ========================================================================== */

function initCores() {
  const root = document.querySelector('[data-cores]');
  if (!root) return;

  const tabs = Array.from(root.querySelectorAll('[role="tab"]'));
  const panels = Array.from(root.querySelectorAll('[role="tabpanel"]'));
  if (!tabs.length || !panels.length) return;

  // Without JS all panels are visible stacked (see CSS). Once we take over,
  // make sure only the active one shows.
  const activate = (index, { focus = false, animate = true } = {}) => {
    tabs.forEach((tab, i) => {
      const selected = i === index;
      tab.setAttribute('aria-selected', String(selected));
      tab.setAttribute('tabindex', selected ? '0' : '-1');
      if (selected && focus) tab.focus();
    });

    panels.forEach((panel, i) => {
      const active = i === index;
      panel.classList.toggle('is-active', active);

      if (active && animate && !prefersReduced) {
        const bits = panel.querySelectorAll('[data-reveal], [data-reveal-child] > *');
        gsap.fromTo(
          Array.from(bits),
          { y: 18, opacity: 0 },
          { y: 0, opacity: 1, duration: 0.6, ease: 'power3.out', stagger: 0.05, overwrite: true }
        );
        bits.forEach((b) => b.setAttribute('data-revealed', 'true'));
      }
    });

    ScrollTrigger.refresh();
  };

  tabs.forEach((tab, i) => {
    tab.addEventListener('click', () => activate(i));

    tab.addEventListener('keydown', (e) => {
      let next = null;

      if (e.key === 'ArrowRight') next = (i + 1) % tabs.length;
      if (e.key === 'ArrowLeft') next = (i - 1 + tabs.length) % tabs.length;
      if (e.key === 'Home') next = 0;
      if (e.key === 'End') next = tabs.length - 1;

      if (next !== null) {
        e.preventDefault();
        activate(next, { focus: true });
      }
    });
  });

  // Show the initially-selected panel.
  const initial = tabs.findIndex((t) => t.getAttribute('aria-selected') === 'true');
  activate(initial >= 0 ? initial : 0, { animate: false });
}

/* ==========================================================================
   Accordion (used on pillar pages)
   ========================================================================== */

function initAccordions() {
  document.querySelectorAll('.acc__trigger').forEach((trigger) => {
    trigger.setAttribute('aria-expanded', 'false');

    trigger.addEventListener('click', () => {
      const item = trigger.closest('.acc__item');
      if (!item) return;
      const open = item.classList.toggle('is-open');
      trigger.setAttribute('aria-expanded', String(open));
    });
  });
}

/* ==========================================================================
   Copy to clipboard
   ========================================================================== */

function initCopy() {
  document.querySelectorAll('[data-copy]').forEach((btn) => {
    const label = btn.querySelector('[data-copy-state]');
    const original = label ? label.textContent : '';

    btn.addEventListener('click', async () => {
      const value = btn.getAttribute('data-copy') || '';

      try {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(value);
        } else {
          // Fallback for non-secure contexts.
          const input = document.createElement('textarea');
          input.value = value;
          input.setAttribute('readonly', '');
          input.style.position = 'absolute';
          input.style.left = '-9999px';
          document.body.appendChild(input);
          input.select();
          document.execCommand('copy');
          input.remove();
        }

        if (label) {
          label.textContent = 'Copied';
          window.setTimeout(() => {
            label.textContent = original;
          }, 1600);
        }
      } catch (err) {
        // Silent failure is fine — the number is visible on screen regardless.
      }
    });
  });
}

/* ==========================================================================
   Custom cursor (fine pointers only)
   ========================================================================== */

function initCursor() {
  if (prefersReduced) return;
  if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
  if (window.innerWidth < 900) return;

  const dot = document.createElement('div');
  dot.className = 'cursor-dot';
  dot.setAttribute('aria-hidden', 'true');
  document.body.appendChild(dot);
  document.documentElement.classList.add('has-custom-cursor');

  const xTo = gsap.quickTo(dot, 'x', { duration: 0.28, ease: 'power3.out' });
  const yTo = gsap.quickTo(dot, 'y', { duration: 0.28, ease: 'power3.out' });

  window.addEventListener(
    'mousemove',
    (e) => {
      dot.classList.add('is-active');
      xTo(e.clientX);
      yTo(e.clientY);
    },
    { passive: true }
  );

  document.addEventListener('mouseleave', () => dot.classList.remove('is-active'));

  // Grow over interactive things.
  document.querySelectorAll('a, button, [role="tab"], input, textarea, .card').forEach((el) => {
    el.addEventListener('mouseenter', () => dot.classList.add('is-hover'));
    el.addEventListener('mouseleave', () => dot.classList.remove('is-hover'));
  });
}

/* ==========================================================================
   WebGL: decide whether to download it at all
   ========================================================================== */

function webglSupported() {
  try {
    const canvas = document.createElement('canvas');
    return !!(
      window.WebGLRenderingContext &&
      (canvas.getContext('webgl2') || canvas.getContext('webgl'))
    );
  } catch (e) {
    return false;
  }
}

function shouldLoadWebGL() {
  if (prefersReduced) return false;
  if (!webglSupported()) return false;

  // Don't ask low-powered devices to run real-time 3D.
  const cores = navigator.hardwareConcurrency || 0;
  if (cores && cores < 4) return false;

  // Skip on small screens — the hero is a different composition there anyway.
  if (window.innerWidth < 720) return false;

  // Respect the user's data preference.
  const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  if (conn && conn.saveData) return false;

  return true;
}

function loadHeroBundle() {
  if (!shouldLoadWebGL()) {
    document.documentElement.classList.add('no-webgl');
    return;
  }

  const src = DATA.heroBundle;
  if (!src) return;

  const script = document.createElement('script');
  script.src = src;
  script.async = true;
  script.onerror = () => document.documentElement.classList.add('no-webgl');
  // The WebGL bundle changes nothing about layout, but parsing 500 KB of
  // three.js and compiling shaders can block the main thread long enough to
  // starve ScrollTrigger. Re-measure once it has settled.
  script.onload = () => window.setTimeout(() => ScrollTrigger.refresh(), 120);
  document.head.appendChild(script);
}

/**
 * Safety net for the reveal system.
 *
 * Scroll-reveals are a progressive enhancement: if the machinery that un-hides
 * an element ever fails — a blocked main thread during heavy WebGL startup, a
 * browser quirk, an extension interfering — the section would stay permanently
 * invisible, which is far worse than missing an animation.
 *
 * This checks, a few seconds in and again after load, whether anything that has
 * already scrolled into or past the viewport is still hidden. If so, it is
 * shown immediately. Elements still below the fold are left alone, so normal
 * scroll reveals are unaffected.
 */
function installRevealSafetyNet() {
  if (prefersReduced) return;

  const rescue = () => {
    document.querySelectorAll('[data-reveal], [data-reveal-child] > *').forEach((el) => {
      if (el.getAttribute('data-revealed') === 'true') return;

      const rect = el.getBoundingClientRect();

      // Only rescue what the reader can already see (or has scrolled past).
      if (rect.top < window.innerHeight && rect.bottom > -Infinity && rect.height > 0) {
        el.setAttribute('data-revealed', 'true');
        gsap.set(el, { opacity: 1, x: 0, y: 0, scale: 1, clearProps: 'clipPath' });
      }
    });
  };

  window.setTimeout(rescue, 4000);
  window.addEventListener('load', () => window.setTimeout(rescue, 800));
}

/* ==========================================================================
   Boot
   ========================================================================== */

function boot() {
  initChrome();
  initCores();
  initAccordions();
  initCopy();

  if (!prefersReduced) {
    initSmoothScroll();
    initCursor();
  }

  initSplits();
  initReveals();
  initCounters();
  installRevealSafetyNet();

  // WebGL is the last thing we ask for — after layout and text are settled.
  const start = () => loadHeroBundle();
  if ('requestIdleCallback' in window) {
    window.requestIdleCallback(start, { timeout: 2500 });
  } else {
    window.setTimeout(start, 600);
  }

  // Keep trigger positions honest once images and fonts land.
  window.addEventListener('load', () => ScrollTrigger.refresh());
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(() => ScrollTrigger.refresh());
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot);
} else {
  boot();
}
